import {
  CognitoUser,
  CognitoUserAttribute,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  GetUserCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { userPool, cognitoConfig } from '../../config/cognito';
import { storageService } from '../../utils/storage.utils';
import { extractUserFromToken, isTokenExpired } from '../../utils/cognito.token';
import type {
  AuthTokens,
  LoginCredentials,
  SignupData,
  CognitoUser as CognitoUserType,
  PasswordResetData,
} from '../../types';

// Type for OAuth token response
interface OAuthTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

class CognitoService {
  private client: CognitoIdentityProviderClient;

  constructor() {
    this.client = new CognitoIdentityProviderClient({
      region: cognitoConfig.region,
    });
  }

  // ============================================
  // EMAIL/PASSWORD AUTHENTICATION
  // ============================================

  /**
   * Sign up a new user with email and password
   */
  async signUp(data: SignupData): Promise<CognitoUser> {
    return new Promise((resolve, reject) => {
      const attributeList = [
        new CognitoUserAttribute({
          Name: 'email',
          Value: data.email,
        }),
        new CognitoUserAttribute({
          Name: 'name',
          Value: data.name,
        }),
      ];

      if (data.role) {
        attributeList.push(
          new CognitoUserAttribute({
            Name: 'custom:role',
            Value: data.role,
          })
        );
      }

      userPool.signUp(
        data.email,
        data.password,
        attributeList,
        [],
        (err, result) => {
          if (err) {
            reject(this.handleError(err));
            return;
          }
          if (!result) {
            reject(new Error('Signup failed: No result returned'));
            return;
          }
          resolve(result.user);
        }
      );
    });
  }

  /**
   * Confirm email verification code
   */
  async confirmSignUp(email: string, code: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) {
          reject(this.handleError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Resend confirmation code
   */
  async resendConfirmationCode(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.resendConfirmationCode((err) => {
        if (err) {
          reject(this.handleError(err));
          return;
        }
        resolve();
      });
    });
  }

  /**
   * Sign in with email and password
   */
  async signIn(credentials: LoginCredentials): Promise<{
    user: CognitoUserType;
    tokens: AuthTokens;
  }> {
    return new Promise((resolve, reject) => {
      const authenticationDetails = new AuthenticationDetails({
        Username: credentials.email,
        Password: credentials.password,
      });

      const cognitoUser = new CognitoUser({
        Username: credentials.email,
        Pool: userPool,
      });

      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (session: CognitoUserSession) => {
          const tokens = this.extractTokensFromSession(session);
          const user = extractUserFromToken(tokens.idToken);

          storageService.setTokens(tokens);
          storageService.setUserData(user);

          resolve({ user, tokens });
        },
        onFailure: (err) => {
          reject(this.handleError(err));
        },
        newPasswordRequired: (userAttributes) => {
          reject({
            code: 'NewPasswordRequired',
            message: 'New password required',
            userAttributes,
          });
        },
      });
    });
  }

  /**
   * Complete new password challenge
   */
  async completeNewPassword(
    email: string,
    newPassword: string
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.completeNewPasswordChallenge(
        newPassword,
        {},
        {
          onSuccess: () => resolve(),
          onFailure: (err) => reject(this.handleError(err)),
        }
      );
    });
  }

  // ============================================
  // GOOGLE OAUTH AUTHENTICATION
  // ============================================

  /**
   * Generate Google OAuth login URL and redirect
   */
  initiateGoogleLogin(): void {
    const state = this.generateRandomState();
    storageService.setAuthState(state);

    const params: Record<string, string> = {
      client_id: cognitoConfig.userPoolClientId||'',
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: cognitoConfig.redirectUri ||'',
      state,
      identity_provider: 'Google',
    };

    const queryString = new URLSearchParams(params).toString();
    const loginUrl = `https://${cognitoConfig.cognitoDomain}/oauth2/authorize?${queryString}`;
    
    window.location.href = loginUrl;
  }

  /**
   * Handle OAuth callback and exchange code for tokens
   */
  async handleOAuthCallback(code: string, state: string): Promise<{
    user: CognitoUserType;
    tokens: AuthTokens;
  }> {
    // Validate state to prevent CSRF
    const storedState = storageService.getAuthState();
    if (state !== storedState) {
      throw new Error('Invalid state parameter - possible CSRF attack');
    }
    storageService.clearAuthState();

    // Exchange authorization code for tokens
    const tokens = await this.exchangeCodeForTokens(code);

    // Extract user info from ID token
    const user = extractUserFromToken(tokens.idToken);

    // Store tokens and user data
    storageService.setTokens(tokens);
    storageService.setUserData(user);

    return { user, tokens };
  }

  /**
   * Exchange authorization code for tokens
   * FIXED: Proper type handling for URLSearchParams
   */
  private async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    // Create properly typed object for URLSearchParams
    const tokenRequestParams: Record<string, string> = {
      grant_type: 'authorization_code',
      client_id: cognitoConfig.userPoolClientId || '',
      code: code,
      redirect_uri: cognitoConfig.redirectUri || '',
    };

    const params = new URLSearchParams(tokenRequestParams);

    const tokenEndpoint = `https://${cognitoConfig.cognitoDomain}/oauth2/token`;

    try {
      const response = await fetch(tokenEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        let errorMessage = 'Token exchange failed';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error_description || errorData.error || errorMessage;
        } catch {
          errorMessage = `Token exchange failed with status ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json() as OAuthTokenResponse;

      // Validate response has required fields
      if (!data.access_token || !data.id_token || !data.refresh_token) {
        throw new Error('Invalid token response: missing required tokens');
      }

      return {
        accessToken: data.access_token,
        idToken: data.id_token,
        refreshToken: data.refresh_token,
      };
    } catch (error) {
      console.error('Token exchange error:', error);
      throw this.handleError(error);
    }
  }

  // ============================================
  // TOKEN MANAGEMENT
  // ============================================

  /**
   * Refresh access token using refresh token
   */
  async refreshTokens(): Promise<AuthTokens> {
    const tokens = storageService.getTokens();
    if (!tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const command = new InitiateAuthCommand({
        AuthFlow: 'REFRESH_TOKEN_AUTH',
        ClientId: cognitoConfig.userPoolClientId,
        AuthParameters: {
          REFRESH_TOKEN: tokens.refreshToken,
        },
      });

      const response = await this.client.send(command);

      if (!response.AuthenticationResult?.AccessToken || !response.AuthenticationResult?.IdToken) {
        throw new Error('Token refresh failed: missing tokens in response');
      }

      const newTokens: AuthTokens = {
        accessToken: response.AuthenticationResult.AccessToken,
        idToken: response.AuthenticationResult.IdToken,
        refreshToken: tokens.refreshToken, // Refresh token doesn't change
      };

      storageService.setTokens(newTokens);

      // Update user data from new ID token
      const user = extractUserFromToken(newTokens.idToken);
      storageService.setUserData(user);

      return newTokens;
    } catch (error) {
      console.error('Token refresh error:', error);
      storageService.clearAll();
      throw this.handleError(error);
    }
  }

  /**
   * Get current session (with automatic token refresh)
   */
  async getCurrentSession(): Promise<{
    user: CognitoUserType;
    tokens: AuthTokens;
  } | null> {
    let tokens = storageService.getTokens();

    if (!tokens) {
      return null;
    }

    // Check if access token is expired
    if (isTokenExpired(tokens.accessToken)) {
      try {
        tokens = await this.refreshTokens();
      } catch (error) {
        console.error('Session refresh failed:', error);
        return null;
      }
    }

    const user = extractUserFromToken(tokens.idToken);

    return { user, tokens };
  }

  // ============================================
  // PASSWORD MANAGEMENT
  // ============================================

  /**
   * Initiate password reset
   */
  async forgotPassword(email: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: email,
        Pool: userPool,
      });

      cognitoUser.forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(this.handleError(err)),
      });
    });
  }

  /**
   * Complete password reset with verification code
   */
  async resetPassword(data: PasswordResetData): Promise<void> {
    return new Promise((resolve, reject) => {
      const cognitoUser = new CognitoUser({
        Username: data.email,
        Pool: userPool,
      });

      cognitoUser.confirmPassword(data.code, data.newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(this.handleError(err)),
      });
    });
  }

  /**
   * Change password for authenticated user
   */
  async changePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<void> {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      throw new Error('No authenticated user');
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null) => {
        if (err) {
          reject(this.handleError(err));
          return;
        }

        cognitoUser.changePassword(oldPassword, newPassword, (err) => {
          if (err) {
            reject(this.handleError(err));
            return;
          }
          resolve();
        });
      });
    });
  }

  // ============================================
  // USER MANAGEMENT
  // ============================================

  /**
   * Get current user attributes
   */
  async getUserAttributes(): Promise<Record<string, string>> {
    const tokens = storageService.getTokens();

    if (!tokens) {
      throw new Error('No authenticated user');
    }

    try {
      const command = new GetUserCommand({
        AccessToken: tokens.accessToken,
      });

      const response = await this.client.send(command);

      const attributes: Record<string, string> = {};
      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name] = attr.Value;
        }
      });

      return attributes;
    } catch (error) {
      throw this.handleError(error);
    }
  }

  /**
   * Update user attributes
   */
  async updateUserAttributes(
    attributes: Record<string, string>
  ): Promise<void> {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      throw new Error('No authenticated user');
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null) => {
        if (err) {
          reject(this.handleError(err));
          return;
        }

        const attributeList = Object.entries(attributes).map(
          ([name, value]) =>
            new CognitoUserAttribute({
              Name: name,
              Value: value,
            })
        );

        cognitoUser.updateAttributes(attributeList, (err) => {
          if (err) {
            reject(this.handleError(err));
            return;
          }
          resolve();
        });
      });
    });
  }

  // ============================================
  // SIGN OUT
  // ============================================

  /**
   * Sign out user (works for both email and Google OAuth)
   */
  async signOut(): Promise<void> {
    const cognitoUser = userPool.getCurrentUser();

    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Clear local storage
    storageService.clearAll();

    // Create properly typed params for logout
    const logoutParams: Record<string, string> = {
      client_id: cognitoConfig.userPoolClientId || '',
      logout_uri: cognitoConfig.logoutUri || '',
    };

    const params = new URLSearchParams(logoutParams);

    // Redirect to Cognito logout endpoint (clears OAuth session)
    window.location.href = `https://${cognitoConfig.cognitoDomain}/logout?${params}`;
  }

  /**
   * Global sign out (revokes all tokens)
   */
  async globalSignOut(): Promise<void> {
    const cognitoUser = userPool.getCurrentUser();

    if (!cognitoUser) {
      throw new Error('No authenticated user');
    }

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null) => {
        if (err) {
          reject(this.handleError(err));
          return;
        }

        cognitoUser.globalSignOut({
          onSuccess: () => {
            storageService.clearAll();
            resolve();
          },
          onFailure: (err) => reject(this.handleError(err)),
        });
      });
    });
  }

  // ============================================
  // UTILITY METHODS
  // ============================================

  private extractTokensFromSession(session: CognitoUserSession): AuthTokens {
    return {
      accessToken: session.getAccessToken().getJwtToken(),
      idToken: session.getIdToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
    };
  }

  private generateRandomState(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, (byte) =>
      byte.toString(16).padStart(2, '0')
    ).join('');
  }

  private handleError(error: unknown): Error {
    // Handle AWS SDK errors
    if (error && typeof error === 'object' && 'name' in error) {
      const awsError = error as { name: string; message?: string };
      return new Error(awsError.message || awsError.name);
    }

    // Handle Cognito errors
    if (error && typeof error === 'object' && 'code' in error) {
      const cognitoError = error as { code: string; message?: string };
      return new Error(cognitoError.message || cognitoError.code);
    }

    // Handle Error instances
    if (error instanceof Error) {
      return error;
    }

    // Fallback for unknown errors
    return new Error('An unknown error occurred');
  }
}

export const cognitoService = new CognitoService();